import {startTransition, useCallback, useMemo} from 'react'

import {useMergeMethodContext} from '../contexts/MergeMethodContext'
import {useMergeBoxPageDataQueryKey, useSuspenseMergeBoxPageData} from '../page-data/loaders/use-merge-box-page-data'
import {useStackEntryMergeabilityQueries} from '../page-data/loaders/use-stack-mergeability-page-data'
import {useStatusChecksPageData} from '../page-data/loaders/use-status-checks-page-data'
import {
  EMPTY_CHANNELS,
  useMergeabilityLiveUpdates,
  type MergeabilityLiveUpdateChannels,
} from './use-mergeability-live-updates'
import {useStatusChecksLiveUpdates} from './use-status-checks-live-updates'
import {MergeBoxStatusCalculator} from '../helpers/merge-box-status-calculator/merge-box-status-calculator'
import {useFeatureStates} from '@github-ui/pull-requests/use-feature-state'
import {useStackRebaseStatus} from './mutations/use-stack-rebase-mutation'

export function useMergeboxStatusCalculator({
  headSha,
  channels,
}: {
  headSha: string
  channels: MergeabilityLiveUpdateChannels | null
}) {
  const {mergeMethod} = useMergeMethodContext()
  const {data, refetch} = useSuspenseMergeBoxPageData({mergeMethod})
  const queryKey = useMergeBoxPageDataQueryKey()
  const {
    data: statusChecksData,
    data: {aliveChannels},
  } = useStatusChecksPageData({pullRequestHeadSha: headSha})

  const refetchQuery = useCallback(() => {
    startTransition(() => {
      refetch({cancelRefetch: false})
    })
  }, [refetch])

  useMergeabilityLiveUpdates({refetchQuery, queryKey, channels: channels ?? EMPTY_CHANNELS})
  useStatusChecksLiveUpdates(aliveChannels.commitHeadShaChannel, headSha)

  const {pullRequest, mergeRequirements} = data
  const featureStates = useFeatureStates()
  const {isRebasing} = useStackRebaseStatus()

  const stackMergeabilityQueries = useStackEntryMergeabilityQueries({
    mergeRequirements,
    currentPullRequestId: pullRequest.id,
    mergeMethod,
    bypassRequirements: false,
  })

  const calculator = useMemo(
    () =>
      new MergeBoxStatusCalculator(
        pullRequest,
        mergeRequirements,
        statusChecksData,
        featureStates,
        stackMergeabilityQueries,
        isRebasing,
      ),
    [pullRequest, mergeRequirements, statusChecksData, featureStates, stackMergeabilityQueries, isRebasing],
  )

  return {calculator, pullRequest, mergeRequirements, statusChecksData}
}
